import { ChangeDetectorRef, Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject, output, input, signal, linkedSignal } from '@angular/core';
import {Client, Package} from '@app/modules/shared/models';
import {
  getPackage, getPackageError,
  getPackageSuccess,
  IPackageState, PackagesAdd, PackagesGet, PackagesUpdate,
  ResetPackageState
} from '@app/stores';
import {Constants} from '@app/consts';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbInputFormComponent } from '../../../_components/unified/sb-form/sb-form.component';

@Component({
  selector: 'app-package-add',
  templateUrl: './package-add.component.html',
  styleUrls: ['./package-add.component.css'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, SbInputFormComponent, ReactiveFormsModule]
})
export class PackageAddComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private packageStore = inject<Store<IPackageState>>(Store);
  private toastrService = inject(ToastrService);
  constants = inject(Constants);
  private cdr = inject(ChangeDetectorRef);


  addPackageForm: FormGroup<{
    name: FormControl<string | null>;
    project_count_limit: FormControl<string | null>;
    project_campaign_count_limit: FormControl<string | null>;
    project_influencer_count_limit: FormControl<string | null>;
  }>;
  unsubscriber = new Subject();
  readonly submitted = signal(false);
  /** Route param on the edit page, or input when opened as a modal. */
  readonly packageIdInput = input<number>(undefined, {alias: 'packageId'});
  readonly packageId = linkedSignal(() => this.packageIdInput());
  /** True when opened inside an NgbModal via EntityFormModalService. */
  readonly isModal = input(false);
  readonly modalDone = output<'saved' | 'cancelled'>();

  constructor() {
    this.packageStore.dispatch(ResetPackageState({params: {}}));
    this.subscribeStores();
  }

  subscribeStores() {
    this.packageStore.pipe(select(getPackageError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error);
        }
      });
    this.packageStore.pipe(select(getPackageSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.submitted()) {
            this.submitted.set(false);
            if (this.isModal()) {
              this.modalDone.emit('saved');
              return;
            }
            this.cancelClick();
          }
        }
      });

    this.packageStore.pipe(select(getPackage))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(pkg => {
        if (pkg) {
          this.addPackageForm.patchValue({...pkg});
          this.cdr.markForCheck();
        }
      });
  }


  ngOnInit(): void {
    this.addPackageForm = this.formBuilder.group({
      name: ['', Validators.required],
      project_count_limit: [null as string | null],
      project_campaign_count_limit: [null as string | null],
      project_influencer_count_limit: [null as string | null],
    });
    if (this.isModal()) {
      // Opened via EntityFormModalService: packageId comes from the input.
      const packageId = this.packageId();
      if (packageId) {
        this.loadPackage(packageId);
      }
    } else {
      this.route.paramMap.subscribe(params => {
        if (params.has('id')) {
          this.packageId.set(+params.get('id'));
          this.cdr.markForCheck();
          this.loadPackage(this.packageId());
        }
      });
    }
  }

  get f() {
    return this.addPackageForm.controls;
  }

  loadPackage(packageId: number) {
    this.packageStore.dispatch(PackagesGet({packageId}));
  }

  savePackage() {
    this.submitted.set(true);
    if (this.addPackageForm.controls.name.status === 'INVALID') {
      return;
    }
    const packageId = this.packageId();
    if (packageId) {
      this.packageStore.dispatch(PackagesUpdate({packageId: packageId, body: this.addPackageForm.value}));
    } else {
      this.packageStore.dispatch(PackagesAdd({body: this.addPackageForm.value}));
    }
  }

  cancelClick() {
    if (this.isModal()) {
      this.modalDone.emit('cancelled');
      return;
    }
    this.router.navigate(['system-admin/packages']);
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

}
